import { LISTENING_OFF_MS } from "@aybuben/client/device/listening.ts";
import type { ListeningPause } from "@aybuben/client/lesson/listening-gate.ts";
import {
	createLessonRunner,
	type LessonDone,
	type LessonMiss,
} from "@aybuben/client/lesson/runner.ts";
import type { Moment as MomentModel } from "@aybuben/client/moments/moments.ts";
import type { Course } from "@aybuben/core/content/course.ts";
import { exerciseType } from "@aybuben/core/exercises/registry.ts";
import type { LessonStep } from "@aybuben/core/lesson.ts";
import type { Silencer } from "@aybuben/core/silent.ts";
import { Suspense, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useStore } from "zustand";

import { play } from "../audio.ts";
import { exerciseView } from "../exercises/view-registry.ts";
import "../exercises/views.ts";
import { haptic } from "../haptics.ts";
import { useStrings, type UiKey } from "../i18n/index.tsx";
import { COL, FOCUS } from "../theme.ts";
import { LessonDock } from "./dock.ts";
import { ownsKey, watchPointer } from "./keys.ts";
import { lazyPart, PartBoundary, SkipPart, usePartError } from "./lazy.tsx";
import { useListening } from "./listening/context.tsx";
import { ListeningScope } from "./listening/Scope.tsx";
import { Btn, CloseBtn } from "./primitives.tsx";
import { useSound } from "./sound.tsx";

// Lazy: the flip path and the overlay stay out of the startup bundle; a lesson
// preloads it on mount, long before its first moment.
const momentPart = lazyPart(() => import("./Moment.tsx").then((m) => m.Moment));
const Moment = momentPart.Part;

const PRAISE: UiKey[] = ["lesson.praise1", "lesson.praise2", "lesson.praise3", "lesson.praise4"];
const hash = (s: string) => [...s].reduce((a, c) => a + c.charCodeAt(0), 0);

interface LessonProps {
	initial: LessonStep[];
	/** Names a wrong pick for the learner's history; see the runner's `course`. */
	course?: Course;
	onQuit: () => void;
	onDone: (result: LessonDone) => void;
	/** A wrong answer, for analytics. */
	onMiss?: (m: LessonMiss) => void;
	/** A moment interstitial opened, for analytics. */
	onMoment?: (m: MomentModel) => void;
	/** Whether the learner has right-answered a sentence before this lesson. */
	composedBefore?: boolean;
	/** Silences the rest of the round on "Can't listen now"; absent when the lesson teaches by sound. */
	silence?: Silencer;
	/** The round was built with listening off: it stays silent to its end, whatever the clock says. */
	muted?: boolean;
	/** The learner tapped "Can't listen now", for analytics. */
	onPaused?: (p: ListeningPause) => void;
	/**
	 * The lesson teaches by sound: "Can't listen now" turns listening off and
	 * hands the round back here to close, rather than muting it.
	 */
	onClose?: (p: ListeningPause) => void;
}

/**
 * The lesson shell. It knows how to run a queue of steps, score them and
 * report a result — and nothing about what any step *is*. Every per-kind
 * decision (what to render, what the action button does, what feedback to
 * show, what counts, what gets bumped, whether to re-ask) is answered by the
 * registry. Adding an exercise type does not touch this file.
 */
export function Lesson({
	initial,
	course,
	onQuit,
	onDone,
	onMiss,
	onMoment,
	composedBefore,
	silence,
	muted: began = false,
	onPaused,
	onClose,
}: LessonProps) {
	const t = useStrings();
	const listening = useListening();
	// Read by the runner's cue callback, which outlives the render it was made in.
	const quiet = useRef(began);
	const onDoneRef = useRef(onDone);
	onDoneRef.current = onDone;
	const onMissRef = useRef(onMiss);
	onMissRef.current = onMiss;
	// Reports the tap in step with the runner, which may end the round in the same call.
	const onPausedRef = useRef(onPaused);
	onPausedRef.current = onPaused;
	const listeningRef = useRef(listening);
	listeningRef.current = listening;
	const onMomentRef = useRef(onMoment);
	onMomentRef.current = onMoment;
	const onMomentError = usePartError("moment");
	useEffect(() => {
		momentPart.preload().catch(() => {});
	}, []);
	// Created once per lesson: the step loop, its scoring and its result live in
	// the runner; this shell renders it and plays its sounds.
	const [runner] = useState(() =>
		createLessonRunner({
			initial,
			course,
			onFeedback: (cue, h) => {
				if (!quiet.current) play(cue);
				if (h) haptic(h);
			},
			onDone: (r) => onDoneRef.current(r),
			onMiss: (m) => onMissRef.current?.(m),
			composedBefore,
			silence,
			onMute: (p) => {
				// Off before the report, so the event is stamped with the new state.
				listeningRef.current.pause();
				onPausedRef.current?.(p);
			},
		}),
	);
	const {
		queue,
		i,
		answer,
		status,
		done,
		moment,
		muted: tapped,
		total,
		dismissMoment,
		setAnswer,
		check,
		cont,
		submit,
	} = useStore(runner);
	quiet.current = began || tapped;
	const onScore = useStore(runner, (s) => s.score);
	const onComplete = useStore(runner, (s) => s.complete);
	// A hidden tab is not time spent on the lesson.
	useEffect(() => {
		const sync = () => runner.getState().setActive(document.visibilityState !== "hidden");
		sync();
		document.addEventListener("visibilitychange", sync);
		return () => document.removeEventListener("visibilitychange", sync);
	}, [runner]);
	const [askQuit, setAskQuit] = useState(false);
	// `i` only ever advances (in `cont`) while `i + 1 < queue.length`, and
	// `queue` only ever grows, so `i` is always a valid index into `queue`.
	const step = queue[i]!;
	const view = exerciseView(step.kind);
	const StepView = view.View;
	const Feedback = view.Feedback;
	const Footer = view.Footer;
	// Where the note about the tap shows: the step after it, until that one is answered.
	const [notedAt, setNotedAt] = useState<number | null>(null);
	const canPause =
		(silence !== undefined || onClose !== undefined) &&
		!tapped &&
		!status &&
		exerciseType(step.kind).heard(step);
	const pause = () => {
		if (!silence && onClose) {
			listeningRef.current.pause();
			onClose({ exercise: step.kind, stepIndex: i, swapped: 0, dropped: 0 });
			return;
		}
		if (!runner.getState().mute()) return;
		setNotedAt(runner.getState().i);
	};
	const recordings = useSound();
	// A step's recording must not outlive it: a feedback word still playing
	// over the next step reads as that step's audio. React runs this cleanup
	// before the next step's autoplay effect, so a heard step still plays.
	useEffect(() => () => recordings.stop(), [step.uid, recordings]);

	// Enter does what the action button would: Check, then Continue. The one
	// handler for the page; a typed answer's own input calls `submit` too.
	// Check ignores it while nothing is answered.
	const latest = useRef(submit);
	latest.current = submit;
	useEffect(watchPointer, []);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Enter" || e.repeat || ownsKey(e.target, e.key)) return;
			e.preventDefault();
			latest.current();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	// The quit dialog is modal for the keyboard too: no key reaches the page's
	// own handlers (Enter, the in-app keyboard) behind it. Its buttons still
	// activate, which is the browser's default action, not a listener's.
	useEffect(() => {
		if (!askQuit) return;
		const onKey = (e: KeyboardEvent) => {
			e.stopPropagation();
			if (e.key === "Escape") setAskQuit(false);
		};
		window.addEventListener("keydown", onKey, true);
		return () => window.removeEventListener("keydown", onKey, true);
	}, [askQuit]);

	// The dock is filled by the step (an in-app keyboard), so the panel grows;
	// the page's foot follows it, or the panel would cover the step.
	const [dock, setDock] = useState<HTMLDivElement | null>(null);
	const panel = useRef<HTMLDivElement>(null);
	const [panelH, setPanelH] = useState(0);
	useEffect(() => {
		const el = panel.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const ro = new ResizeObserver(() => setPanelH(el.offsetHeight));
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	const dockValue = useMemo(() => (dock ? { el: dock, height: panelH } : null), [dock, panelH]);

	const panelBg = status === "right" ? COL.greenSoft : status === "wrong" ? COL.redSoft : COL.bg;

	// Exhaustive over ActionKind, so a new action kind is a compile error here.
	// The explicit return type is load-bearing: without it an unhandled case
	// infers `undefined`, which ReactNode accepts, so the switch would compile
	// clean. Same reason kit.tsx's FaceView is annotated. `| null` is here
	// because the "none" branch genuinely returns null for a view that declares
	// no Footer; it still rejects the `undefined` a missing case would infer.
	const actionSlot = (): ReactElement | null => {
		switch (view.action) {
			case "continue":
				return <Btn onClick={cont}>{t("lesson.continue")}</Btn>;
			case "check":
				return (
					<Btn disabled={answer === null} onClick={check}>
						{t("lesson.check")}
					</Btn>
				);
			case "none":
				return Footer ? <Footer /> : null;
		}
	};

	return (
		// A round that was silent, or went silent on the tap, stays so when the window ends.
		<ListeningScope round={began || tapped}>
			<div className="flex min-h-screen flex-col">
				<div className="flex items-center gap-3 px-4 pt-4">
					<CloseBtn onClick={() => setAskQuit(true)} label={t("lesson.quitAria")} />
					<div
						className="h-4 flex-1 overflow-hidden rounded-full"
						style={{ background: COL.track }}
						role="progressbar"
						aria-valuenow={done}
						aria-valuemax={total}
					>
						<div
							className="h-4 rounded-full"
							style={{
								transition: "width var(--dur-slow) var(--ease-out)",
								width: `${Math.max(4, (done / Math.max(total, 1)) * 100)}%`,
								background: COL.apricot,
							}}
						/>
					</div>
				</div>

				<div className="flex-1 px-5 pt-6" style={{ paddingBottom: Math.max(230, panelH + 24) }}>
					<LessonDock.Provider value={dockValue}>
						<div key={step.uid} className="motion-step">
							<StepView
								key={step.uid}
								step={step}
								answer={answer}
								setAnswer={setAnswer}
								status={status}
								retry={runner.getState().isRetry(step.uid)}
								onComplete={onComplete}
								onScore={onScore}
								onSubmit={submit}
							/>
						</div>
					</LessonDock.Provider>
				</div>

				<div
					ref={panel}
					className="fixed inset-x-0 bottom-0 z-20"
					style={{ background: panelBg, borderTop: status ? "none" : `2px solid ${COL.line}` }}
				>
					{/* Kept mounted, empty unless a step docks something, so a portal never loses its target. */}
					<div ref={setDock} data-testid="lesson-dock" className="mx-auto max-w-md" />
					<div
						className="mx-auto max-w-md px-5 pt-4"
						style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
					>
						{status && (
							<div
								className="motion-rise mb-4"
								style={{ color: status === "right" ? COL.greenDark : COL.redDark }}
								role="status"
							>
								{/* hash(step.uid) % PRAISE.length is always within PRAISE's bounds. */}
								<div className="text-xl font-extrabold">
									{status === "right"
										? t(PRAISE[hash(step.uid) % PRAISE.length]!)
										: t("lesson.correctAnswer")}
								</div>
								{Feedback && <Feedback step={step} ok={status === "right"} answer={answer} />}
							</div>
						)}
						{!status && notedAt === i && (
							<p role="status" className="mb-3 text-center text-sm" style={{ color: COL.mute }}>
								{t("listen.offFor", { n: LISTENING_OFF_MS / 60_000 })}
							</p>
						)}
						{canPause && (
							<button
								type="button"
								onClick={pause}
								className={`mb-3 block w-full rounded-lg py-2 text-sm font-semibold ${FOCUS}`}
								style={{ color: COL.mute }}
							>
								{t("listen.cant")}
							</button>
						)}
						{status ? (
							<Btn onClick={cont} color={status === "right" ? COL.green : COL.red}>
								{status === "right" ? t("lesson.continue") : t("lesson.gotIt")}
							</Btn>
						) : (
							actionSlot()
						)}
					</div>
				</div>

				{moment && (
					// A moment that cannot load is skipped: it is a beat, not a step.
					<PartBoundary fallback={<SkipPart onSkip={dismissMoment} />} onError={onMomentError}>
						<Suspense fallback={null}>
							<Moment
								moment={moment}
								onDone={dismissMoment}
								onShown={() => onMomentRef.current?.(moment)}
							/>
						</Suspense>
					</PartBoundary>
				)}

				{askQuit && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center px-6"
						style={{ background: "rgba(42,27,31,.5)" }}
					>
						<div
							className="w-full max-w-sm rounded-3xl p-6 text-center"
							style={{ background: COL.card }}
						>
							<div className="text-xl font-extrabold">{t("lesson.quitTitle")}</div>
							<p className="mt-2 text-sm" style={{ color: COL.mute }}>
								{t("lesson.quitBody")}
							</p>
							<div className="mt-5 flex flex-col gap-3">
								<Btn onClick={() => setAskQuit(false)}>{t("lesson.quitKeep")}</Btn>
								<button
									onClick={onQuit}
									className={`rounded-lg py-2 font-semibold ${FOCUS}`}
									style={{ color: COL.red }}
								>
									{t("lesson.quitConfirm")}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</ListeningScope>
	);
}
